import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import { EditVariantViewProps } from '@deckManagement/types/KPILibrary.types';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { KPIType } from '@services/api/deckManagement/graphql/schema/API';
import { KpiDimension, PageSidePanelDropDown } from 'types/PageSidePanel.types';

import VariantUsageSummary from './VariantUsageSummary';

/** Maps KPIType enum to available PageSidePanelDropDown display labels */
const KPI_TYPE_LABEL_MAP: Record<KPIType, PageSidePanelDropDown> = {
  [KPIType.BASE]: PageSidePanelDropDown.BASE_KPI, // Only BASE is supported for now
  [KPIType.CALCULATED]: PageSidePanelDropDown.CALCULATED_KPI, // Unused currently
  [KPIType.EXTERNAL]: PageSidePanelDropDown.BASE_KPI, // Unused currently. External renders as Base KPI for now
};

/**
 * Edit Variant view content — 3-column layout with variant details, driver overrides, and usage summary.
 */
const EditVariantView = ({
  selectedRootKPI,
  selectedVariant,
  editVariantName,
  onVariantNameChange,
  driverDisplayOptions,
  placeholderDrivers,
  onDriverChange,
  onDriverToggle,
  usageSummary,
  isUsageSummaryLoading,
  expandedDeckId,
  onExpandChange,
  isDialogMode,
  editVariantActions,
}: EditVariantViewProps) => (
  <>
    <Box className='kpi-library-edit-variant-content'>
      {/* Column 1: Variant Details (name, type, etc.) */}
      <Box className='edit-variant-details'>
        <FormControl size='small' fullWidth margin='dense'>
          <InputLabel id='kpi-type-label'>Type</InputLabel>
          <Select
            labelId='kpi-type-label'
            value={KPI_TYPE_LABEL_MAP[selectedRootKPI.kpi_type]}
            label='Type'
            disabled
            size='small'
          >
            <MenuItem value={KPI_TYPE_LABEL_MAP[selectedRootKPI.kpi_type]}>
              {KPI_TYPE_LABEL_MAP[selectedRootKPI.kpi_type]}
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label='Deckline Name'
          value={selectedRootKPI.root_kpi_name}
          margin='dense'
          size='small'
          disabled
        />
        <TextField
          fullWidth
          label='Variant'
          value={editVariantName}
          onChange={(e) => onVariantNameChange(e.target.value)}
          margin='dense'
          size='small'
          autoComplete='off'
        />
        <List dense className='edit-variant-metadata'>
          <ListItem>
            <ListItemText primary='Created By' />
            <Typography variant='body2'>{selectedVariant.created_by ?? '-'}</Typography>
          </ListItem>
          <ListItem>
            <ListItemText primary='Created On' />
            <Typography variant='body2'>
              {selectedVariant.created_date ? new Date(selectedVariant.created_date).toLocaleDateString() : '-'}
            </Typography>
          </ListItem>
          <ListItem>
            <ListItemText primary='Updated By' />
            <Typography variant='body2'>{selectedVariant.last_updated_by ?? '-'}</Typography>
          </ListItem>
          <ListItem>
            <ListItemText primary='Updated On' />
            <Typography variant='body2'>
              {selectedVariant.last_updated_date
                ? new Date(selectedVariant.last_updated_date).toLocaleDateString()
                : '-'}
            </Typography>
          </ListItem>
        </List>
      </Box>
      {/* Column 2: KPI Driver overrides */}
      <Box className='edit-variant-drivers'>
        <KPIDriversDisplay
          driverOptions={driverDisplayOptions}
          excludeDimensions={[KpiDimension.REPORTING_CURRENCY]}
          renderMode='dimensionPicker'
          placeholderDrivers={placeholderDrivers}
          onChange={onDriverChange}
          onToggleSwitch={onDriverToggle}
        />
      </Box>
      {/* Column 3: Variant usage summary (across decks) */}
      <VariantUsageSummary
        usageSummary={usageSummary}
        isLoading={isUsageSummaryLoading}
        expandedDeckId={expandedDeckId}
        onExpandChange={onExpandChange}
      />
    </Box>
    {isDialogMode && <DialogActions>{editVariantActions}</DialogActions>}
  </>
);

export default EditVariantView;
